import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withStyles } from '@mui/styles';
import {
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionIcon from '@mui/icons-material/Description';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DocumentMSStyle from './DocumentMSStyle';
import { filterActivityLogs, getUsers } from './dmsApi';

// Add custom styles for date picker to match Material-UI inputs
if (typeof document !== 'undefined' && !document.getElementById('date-picker-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'date-picker-styles';
  styleSheet.type = 'text/css';
  styleSheet.innerText = `
    .date-picker-wrapper {
      width: 100%;
    }
    .date-picker-input {
      width: 100% !important;
      height: 48px !important;
      padding: 12px 16px !important;
      font-family: Montserrat !important;
      font-size: 14px !important;
      border-radius: 8px !important;
      border: 1px solid #E0E0E0 !important;
      background-color: #F5F5F5 !important;
      color: #131C4C !important;
      outline: none !important;
      box-sizing: border-box !important;
    }
    .date-picker-input:hover {
      border-color: #BCC0D5 !important;
      background-color: #EEEEEE !important;
    }
    .date-picker-input:focus {
      border-color: #131C4C !important;
      border-width: 1px !important;
      background-color: #FFFFFF !important;
    }
    .date-picker-input::placeholder {
      color: #6E759A !important;
    }
  `;
  document.head.appendChild(styleSheet);
}

const ActivityLog = (props) => {
  const { classes } = props;
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
  });
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    activity_type: '',
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const isInitialMount = useRef(true);

  const activityTypes = [
    { value: 'user_login', label: 'User Login' },
    { value: 'document_created', label: 'Document Created' },
    { value: 'document_assigned', label: 'Document Assigned' },
    { value: 'status_updated', label: 'Status Updated' },
  ];

  const fetchEmployees = async () => {
    try {
      const response = await getUsers('pwc');
      if (response?.results && Array.isArray(response.results)) {
        setEmployees(response.results);
      } else if (Array.isArray(response)) {
        setEmployees(response);
      } else {
        setEmployees([]);
      }
    } catch (err) {
      console.error('Failed to load employees:', err);
      setEmployees([]);
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      const filterData = {
        activity_type: filters.activity_type || '',
        userId: userId || '', // Add userId to payload
        start_date: startDate ? startDate.toISOString().split('T')[0] : '',
        end_date: endDate ? endDate.toISOString().split('T')[0] : '',
      };
      const cleanFilters = Object.fromEntries(
        Object.entries(filterData).filter(([_, v]) => v !== '')
      );
      const data = await filterActivityLogs(cleanFilters);
      setLogs(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError('Failed to load activity logs');
      console.error('Activity logs error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.activity_type, startDate, endDate]);

  const calculateSummary = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const total = logs.length;
    const todayCount = logs.filter((log) => {
      if (!log.timestamp) return false;
      const logDate = new Date(log.timestamp);
      return logDate >= today;
    }).length;
    const weekCount = logs.filter((log) => {
      if (!log.timestamp) return false;
      const logDate = new Date(log.timestamp);
      return logDate >= weekAgo;
    }).length;

    setSummary({ total, today: todayCount, thisWeek: weekCount });
  };

  useEffect(() => {
    fetchEmployees();
    fetchLogs(); // Initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculateSummary();
  }, [logs]);

  // Auto-trigger API call when filters change (debounced)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip on initial mount
    }
    const timeoutId = setTimeout(() => {
      fetchLogs();
    }, 500); // Debounce for 500ms to avoid too many API calls

    return () => clearTimeout(timeoutId);
  }, [fetchLogs]);

  const handleClearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      activity_type: '',
    });
    setStartDate(null);
    setEndDate(null);
    // fetchLogs will be triggered automatically by useEffect
  };

  const getActivityTypeChip = (type, description) => {
    const typeConfig = {
      user_login: {
        label: 'User Login',
        color: '#9C27B0',
        backgroundColor: '#F3E5F5',
      },
      status_updated: {
        label: 'Status Updated',
        color: '#2196F3',
        backgroundColor: '#E3F2FD',
        icon: <ArrowForwardIcon style={{ fontSize: '14px', marginLeft: '4px' }} />,
      },
      document_assigned: {
        label: 'Document Assigned',
        color: '#4CAF50',
        backgroundColor: '#E8F5E9',
        icon: <ArrowForwardIcon style={{ fontSize: '14px', marginLeft: '4px' }} />,
      },
      document_created: {
        label: 'Document Created',
        color: '#4CAF50',
        backgroundColor: '#E8F5E9',
        icon: <DescriptionIcon style={{ fontSize: '14px', marginLeft: '4px' }} />,
      },
    };

    const config = typeConfig[type] || {
      label: type?.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || 'Activity',
      color: '#6E759A',
      backgroundColor: '#F5F5F5',
    };

    return (
      <Chip
        label={
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            {config.label}
            {config.icon}
          </Box>
        }
        size="small"
        style={{
          backgroundColor: config.backgroundColor,
          color: config.color,
          fontWeight: 600,
          fontSize: '12px',
          fontFamily: 'Montserrat',
          height: '24px',
        }}
      />
    );
  };

  const getEmployeeName = (userId) => {
    if (!userId) return '-';
    const employee = employees.find((e) => e._id === userId || e.id === userId || e.user_id === userId);
    return employee ? (employee.name || employee.user_name || employee.email || userId) : userId;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  if (loading && logs.length === 0) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box style={{ padding: '0' }}>
      {/* Summary Cards */}
      <Grid container spacing={3} style={{ marginBottom: '40px' }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            className={classes.summaryCard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Box
              style={{
                width: '56px',
                height: '56px',
                backgroundColor: '#EBE3CA',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <TrendingUpIcon style={{ fontSize: '32px', color: '#131C4C' }} />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography className={classes.summaryValue}>
                {summary.total}
              </Typography>
              <Typography className={classes.summaryLabel}>
                Total Activities
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            className={classes.summaryCard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Box
              style={{
                width: '56px',
                height: '56px',
                backgroundColor: '#EBE3CA',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CalendarTodayIcon style={{ fontSize: '32px', color: '#131C4C' }} />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography className={classes.summaryValue}>
                {summary.today}
              </Typography>
              <Typography className={classes.summaryLabel}>
                Today
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            className={classes.summaryCard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Box
              style={{
                width: '56px',
                height: '56px',
                backgroundColor: '#EBE3CA',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <AccessTimeIcon style={{ fontSize: '32px', color: '#131C4C' }} />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography className={classes.summaryValue}>
                {summary.thisWeek}
              </Typography>
              <Typography className={classes.summaryLabel}>
                This Week
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Paper
        style={{
          padding: '24px',
          borderRadius: '12px',
          marginBottom: '40px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Box style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FilterListIcon style={{ fontSize: '20px', color: '#131C4C' }} />
              <Typography
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#131C4C',
                  fontFamily: 'Montserrat',
                }}
              >
                Filters
              </Typography>
            </Box>
            <Typography
              style={{
                fontSize: '14px',
                color: '#6E759A',
                fontFamily: 'Montserrat',
              }}
            >
              Filter activity logs by date range and type
            </Typography>
          </Box>
          <Button
            className={classes.buttonSecondary}
            onClick={handleClearFilters}
            style={{
              textTransform: 'none',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 500,
              height: '40px',
              padding: '10px 20px',
              borderRadius: '8px',
            }}
          >
            Clear Filters
          </Button>
        </Box>

        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
            alignItems: 'flex-start',
          }}
        >
          <Box style={{ width: '100%' }}>
            <Box className={classes.label} style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#131C4C', fontFamily: 'Montserrat' }}>
              Start Date
            </Box>
            <Box style={{ position: 'relative', width: '100%' }}>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                wrapperClassName="date-picker-wrapper"
                className="date-picker-input"
              />
            </Box>
          </Box>

          <Box style={{ width: '100%' }}>
            <Box className={classes.label} style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#131C4C', fontFamily: 'Montserrat' }}>
              End Date
            </Box>
            <Box style={{ position: 'relative', width: '100%' }}>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                wrapperClassName="date-picker-wrapper"
                className="date-picker-input"
              />
            </Box>
          </Box>

          <Box style={{ width: '100%' }}>
            <Box className={classes.label} style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#131C4C', fontFamily: 'Montserrat' }}>
              Activity Type
            </Box>
            <FormControl 
              className={classes.select} 
              style={{ 
                width: '100%',
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                  backgroundColor: '#F5F5F5',
                },
                '& .MuiInputLabel-root': {
                  display: 'none',
                },
              }}
            >
              <InputLabel 
                style={{ 
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  display: 'none',
                }}
              >
                Activity Type
              </InputLabel>
              <Select
                value={filters.activity_type}
                onChange={(e) => setFilters({ ...filters, activity_type: e.target.value })}
                displayEmpty
                style={{ 
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  height: '48px',
                }}
              >
                <MenuItem value="" style={{ fontFamily: 'Montserrat', fontSize: '14px' }}>
                  All Types
                </MenuItem>
                {activityTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value} style={{ fontFamily: 'Montserrat', fontSize: '14px' }}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {error && <Box className={classes.error}>{error}</Box>}

      {/* Activity History Table */}
      <Box>
        <Box style={{ marginBottom: '24px' }}>
          <Typography
            variant="h5"
            style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#131C4C',
              marginBottom: '4px',
              fontFamily: 'Montserrat',
              lineHeight: '1.2',
            }}
          >
            Activity History
          </Typography>
          <Typography
            variant="body2"
            style={{
              fontSize: '14px',
              color: '#6E759A',
              fontFamily: 'Montserrat',
              lineHeight: '1.4',
            }}
          >
            Complete history of all system activities
          </Typography>
        </Box>

        <TableContainer
          component={Paper}
          style={{
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
          }}
        >
          <Table className={classes.table} sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>
                  Timestamp
                </TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>
                  Type
                </TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>
                  User
                </TableCell>
                <TableCell className={classes.tableHeader}>Description</TableCell>
                <TableCell className={classes.tableHeader}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} style={{ textAlign: 'center', padding: '48px' }}>
                    <Typography style={{ color: '#6E759A', fontSize: '14px', fontFamily: 'Montserrat' }}>
                      No activity logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, index) => (
                  <TableRow key={index} className={classes.tableRow}>
                    <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#131C4C',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {formatTimestamp(log.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                      {getActivityTypeChip(log.activity_type, log.description)}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#131C4C',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {(() => {
                          const privilege = localStorage.getItem('previlage') || sessionStorage.getItem('previlage');
                          if (privilege === 'dms_user') {
                            // For user privilege, show assigned_by_name
                            return log.assigned_by_name || '-';
                          }
                          // For other privileges, use existing logic
                          return log.user || getEmployeeName(log.user_id) || '-';
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#131C4C',
                          fontFamily: 'Montserrat',
                          maxWidth: '400px',
                        }}
                      >
                        {log.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#6E759A',
                          fontFamily: 'Montserrat',
                          maxWidth: '300px',
                        }}
                      >
                        {log.details || log.additional_info || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default withStyles(DocumentMSStyle)(ActivityLog);
